import merge from 'lodash-es/merge';
import noop from 'lodash-es/noop';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/data-access';
import {
  deleteDefaultOrIncompleteCurrentSort,
  getLocationQueryWithSearchState,
  getSearchParams,
  searchStateFromLocationQuery,
} from '@jane/search/util';

interface SearchContext<T> {
  indexPrefix: string;
  resetSearchState: (nextSearchState?: Partial<JaneSearchState<T>>) => void;
  searchState: JaneSearchState<T>;
  setSearchState: (searchState: Partial<JaneSearchState<T>>) => void;
}

const buildSearchState = <T,>(
  initialSearchParams: JaneSearchState<T> = {}
): JaneSearchState<T> => ({
  bucketFilters: {},
  filters: {},
  page: 0,
  rangeFilters: {},
  searchText: '',
  ...initialSearchParams,
});

const SearchContext = createContext<SearchContext<unknown>>({
  indexPrefix: '',
  resetSearchState: noop,
  searchState: buildSearchState(),
  setSearchState: noop,
});
SearchContext.displayName = 'Search';

export const useSearchContext = <T extends object = AlgoliaProduct>() =>
  useContext<SearchContext<T>>(SearchContext);

type SearchProviderProps<T> = PropsWithChildren<{
  indexPrefix: string;
  initialSearchState?: Partial<JaneSearchState<T>>;
  noUrlChanges?: boolean;
  onSearchStateChange?: (nextSearchState: JaneSearchState<T>) => void;
}>;

export const SearchProvider = <T extends object = AlgoliaProduct>({
  children,
  indexPrefix,
  initialSearchState,
  noUrlChanges,
  onSearchStateChange,
}: SearchProviderProps<T>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const shouldReplaceRef = useRef(false);

  const locationQueryState = useMemo(
    () => searchStateFromLocationQuery(location.search),
    [location.search]
  );

  const [searchState, setSearchState] = useState<JaneSearchState<T>>(
    buildSearchState(merge(initialSearchState, locationQueryState))
  );

  useEffect(() => {
    if (noUrlChanges) {
      return;
    }

    const encodedSearchState = getLocationQueryWithSearchState(
      location.search,
      searchState
    );
    if (location.search.replace('?', '') !== encodedSearchState) {
      navigate(
        {
          ...location,
          search: encodedSearchState,
        },
        { replace: shouldReplaceRef.current }
      );
    }
  }, [JSON.stringify(searchState)]);

  useEffect(() => {
    setSearchState((prevSearchState) => {
      const nextSearchState = getSearchParams(locationQueryState);

      if (!Object.keys(nextSearchState).length) {
        return nextSearchState;
      }
      const result = { ...prevSearchState, ...nextSearchState };
      deleteDefaultOrIncompleteCurrentSort(result);

      return result;
    });
  }, [location.search]);

  const setState = useCallback(
    (nextSearchState: Partial<JaneSearchState<T>>) => {
      setSearchState((prevSearchState) => {
        const searchState = {
          ...prevSearchState,
          ...nextSearchState,
        };

        onSearchStateChange?.(searchState);

        return searchState;
      });
    },
    [onSearchStateChange]
  );

  const resetSearchState = useCallback(
    (nextSearchState: Partial<JaneSearchState<T>> = {}) => {
      setSearchState(() => {
        onSearchStateChange?.(nextSearchState);

        return nextSearchState;
      });
    },
    [onSearchStateChange]
  );

  const contextValue = useMemo(
    () => ({
      indexPrefix,
      resetSearchState,
      searchState,
      setSearchState: setState,
    }),
    [indexPrefix, resetSearchState, searchState, setState]
  );

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};
